/**
 * Device Provisioning Page — Control Portal
 * Flow 1: Receive device → Assign to tenant → Generate Device QR → Generate Passport QR Book → Ship
 */
import { useState, useEffect } from 'react';
import { controlApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Cpu, QrCode, BookOpen, Truck, CheckCircle, ArrowRight,
  Loader2, Tag, Package, Send, Copy, Download
} from 'lucide-react';

export default function DeviceProvisioningPage() {
  const [devices, setDevices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Assign modal
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  // QR View modal
  const [qrOpen, setQrOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  // Passport QR Book modal
  const [bookOpen, setBookOpen] = useState(false);
  const [bookTenantId, setBookTenantId] = useState('');
  const [bookCount, setBookCount] = useState(1000);
  const [generatingBook, setGeneratingBook] = useState(false);
  const [bookResult, setBookResult] = useState(null);

  // Ship modal
  const [shipOpen, setShipOpen] = useState(false);
  const [shipTracking, setShipTracking] = useState('');
  const [shipCourier, setShipCourier] = useState('');
  const [shipping, setShipping] = useState(false);

  useEffect(() => {
    fetchData();
     
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [devRes, clientRes] = await Promise.all([
        controlApi.getRegistryDevices({ filter: 'all' }),
        controlApi.getClientsForDispatch()
      ]);
      setDevices(devRes.data || []);
      setClients(clientRes.data || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Step 3-5: Assign to tenant + generate Device QR
  const handleAssign = async () => {
    if (!selectedTenant || !selectedDevice) return;
    try {
      setAssigning(true);
      const res = await controlApi.assignDeviceToTenant(selectedDevice.id, {
        registry_id: selectedDevice.id,
        tenant_id: selectedTenant
      });
      setAssignResult(res.data);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  // View Device QR
  const handleViewQR = async (device) => {
    try {
      setQrLoading(true);
      setQrOpen(true);
      const res = await controlApi.getDeviceQR(device.id);
      setQrData(res.data);
    } catch (err) {
      toast.error('Failed to load QR data');
    } finally {
      setQrLoading(false);
    }
  };

  // Step 6: Generate Passport QR Book
  const handleGenerateBook = async () => {
    if (!bookTenantId) return;
    try {
      setGeneratingBook(true);
      const res = await controlApi.generatePassportQRBook({
        tenant_id: bookTenantId,
        label_count: bookCount
      });
      setBookResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Book generation failed');
    } finally {
      setGeneratingBook(false);
    }
  };

  // Step 7: Ship to tenant
  const handleShip = async () => {
    try {
      setShipping(true);
      const res = await controlApi.shipToTenant({
        passport_batch_id: bookResult?.batch_id || null,
        tracking_number: shipTracking || null,
        courier: shipCourier || null
      });
      toast.success(res.data.message);
      setShipOpen(false);
      setBookResult(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Shipment failed');
    } finally {
      setShipping(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-slate-100 text-slate-600',
      'in_stock': 'bg-green-100 text-green-700',
      'provisioned': 'bg-blue-100 text-blue-700',
      'dispatched': 'bg-purple-100 text-purple-700'
    };
    return colors[status?.toLowerCase()] || 'bg-slate-100 text-slate-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="device-provisioning-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="provisioning-title">Device Provisioning</h1>
          <p className="text-slate-400 text-sm">Flow 1: Receive IMEI → Assign to Tenant → Device QR → Passport QR Book → Ship</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setBookOpen(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="generate-book-btn">
            <BookOpen className="w-4 h-4 mr-2" />Generate Passport QR Book
          </Button>
        </div>
      </div>

      {/* Flow Steps Visual */}
      <div className="grid grid-cols-5 gap-2">
        {[
          { icon: Cpu, label: 'Receive IMEI', desc: 'From manufacturer', step: 1 },
          { icon: Tag, label: 'Assign to Tenant', desc: 'Link device_id + tenant_id', step: 3 },
          { icon: QrCode, label: 'Device QR', desc: 'Permanent, paste on body', step: 5 },
          { icon: BookOpen, label: 'Passport QR Book', desc: '1000+ DORMANT labels', step: 6 },
          { icon: Truck, label: 'Ship Together', desc: 'Device + QR roll → Tenant', step: 7 }
        ].map((s) => (
          <div key={`step-${s.step}`} className="relative">
            <Card className="bg-slate-800 border-slate-700 h-full">
              <CardContent className="p-3 text-center">
                <div className="w-8 h-8 mx-auto mb-1 rounded-full bg-blue-600/20 flex items-center justify-center">
                  <s.icon className="w-4 h-4 text-blue-400" />
                </div>
                <p className="text-white text-xs font-semibold">Step {s.step}</p>
                <p className="text-slate-300 text-xs">{s.label}</p>
                <p className="text-slate-500 text-[10px] mt-0.5">{s.desc}</p>
              </CardContent>
            </Card>
            {s.step < 7 && <ArrowRight className="absolute -right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 z-10" />}
          </div>
        ))}
      </div>

      {/* Devices Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <Cpu className="w-5 h-5 text-blue-400" />Registered Devices ({devices.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-slate-400">
                  <th className="text-left py-2 px-2">Device UID</th>
                  <th className="text-left py-2">IMEI</th>
                  <th className="text-center py-2">Type</th>
                  <th className="text-center py-2">QC</th>
                  <th className="text-center py-2">Status</th>
                  <th className="text-left py-2">Tenant</th>
                  <th className="text-right py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.map(d => (
                  <tr key={d.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="py-2.5 px-2 font-mono text-white">{d.device_uid}</td>
                    <td className="text-slate-400 font-mono text-xs">{d.imei || '—'}</td>
                    <td className="text-center">
                      <Badge className="bg-blue-900/30 text-blue-400 text-xs">{d.device_type}</Badge>
                    </td>
                    <td className="text-center">
                      <Badge className={
                        d.qc_status === 'passed' ? 'bg-green-900/30 text-green-400' :
                        d.qc_status === 'failed' ? 'bg-red-900/30 text-red-400' :
                        'bg-amber-900/30 text-amber-400'
                      }>{d.qc_status}</Badge>
                    </td>
                    <td className="text-center">
                      <Badge className={getStatusColor(d.provisioning_status)}>{d.provisioning_status}</Badge>
                    </td>
                    <td className="text-slate-300 text-xs">{d.provisioned_to || '—'}</td>
                    <td className="text-right px-2">
                      <div className="flex items-center justify-end gap-1">
                        {d.provisioning_status === 'new' && d.qc_status === 'passed' && (
                          <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 h-7 px-2 text-xs" onClick={() => { setSelectedDevice(d); setAssignResult(null); setSelectedTenant(''); setAssignOpen(true); }} data-testid={`assign-${d.device_uid}`}>
                            <Tag className="w-3 h-3 mr-1" />Assign
                          </Button>
                        )}
                        {d.provisioning_status !== 'new' && (
                          <Button size="sm" variant="ghost" className="text-purple-400 hover:text-purple-300 h-7 px-2 text-xs" onClick={() => handleViewQR(d)} data-testid={`qr-${d.device_uid}`}>
                            <QrCode className="w-3 h-3 mr-1" />QR
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Assign to Tenant Modal */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Tag className="w-5 h-5 text-blue-400" />Assign Device to Tenant
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedDevice?.device_uid} — IMEI: {selectedDevice?.imei || 'N/A'}
            </DialogDescription>
          </DialogHeader>

          {assignResult ? (
            <div className="space-y-4 py-2">
              <div className="text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="text-green-400 font-semibold">Device Assigned</p>
                <p className="text-slate-400 text-sm">{assignResult.message}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Device</span><span className="font-mono text-white">{assignResult.device_uid}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Tenant</span><span className="text-white">{assignResult.tenant_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Status</span><Badge className="bg-green-900/30 text-green-400">{assignResult.provisioning_status}</Badge></div>
              </div>
              <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-3">
                <p className="text-blue-300 text-xs font-semibold mb-1 flex items-center gap-1"><QrCode className="w-3 h-3" />Device QR (paste on hardware body)</p>
                <p className="font-mono text-xs text-blue-400 break-all">{assignResult.device_qr_data}</p>
                <p className="text-slate-500 text-[10px] mt-1">{assignResult.device_qr_label}</p>
                <Button size="sm" variant="ghost" className="mt-2 text-blue-400 h-6 text-xs" onClick={() => { navigator.clipboard.writeText(assignResult.device_qr_data); toast.success('QR data copied'); }}>
                  <Copy className="w-3 h-3 mr-1" />Copy QR Data
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Select Tenant / Client</label>
                <select value={selectedTenant} onChange={(e) => setSelectedTenant(e.target.value)} className="w-full rounded-md border border-slate-600 bg-slate-900 text-white p-2.5" data-testid="assign-tenant-select">
                  <option value="">Select tenant...</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-slate-500">This will generate a permanent Device QR encoding device_id + tenant_id. Paste this QR on the hardware body.</p>
            </div>
          )}

          <DialogFooter>
            {assignResult ? (
              <Button onClick={() => setAssignOpen(false)} className="bg-blue-600 hover:bg-blue-700">Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setAssignOpen(false)} className="border-slate-600 text-slate-300">Cancel</Button>
                <Button onClick={handleAssign} disabled={assigning || !selectedTenant} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-assign-btn">
                  {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Tag className="w-4 h-4 mr-2" />}
                  Assign & Generate QR
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device QR View Modal */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <QrCode className="w-5 h-5 text-blue-400" />Device QR Code
            </DialogTitle>
          </DialogHeader>
          {qrLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
          ) : qrData ? (
            <div className="space-y-4 py-2 text-center">
              <div className="bg-white rounded-lg p-6 mx-auto w-48 h-48 flex items-center justify-center">
                <QrCode className="w-24 h-24 text-slate-900" />
              </div>
              <div className="space-y-2 text-sm">
                <p className="font-mono font-bold text-white">{qrData.device_uid}</p>
                <p className="text-slate-400">{qrData.tenant_name} · {qrData.device_type}</p>
                <Badge className={getStatusColor(qrData.provisioning_status)}>{qrData.provisioning_status}</Badge>
              </div>
              <div className="bg-slate-900 rounded p-2 text-left">
                <p className="text-slate-500 text-[10px] mb-1">QR Data (permanent)</p>
                <p className="font-mono text-xs text-blue-400 break-all">{qrData.qr_data}</p>
              </div>
              <p className="text-slate-600 text-[10px]">{qrData.note}</p>
              <Button size="sm" variant="outline" className="border-blue-600 text-blue-400" onClick={() => { navigator.clipboard.writeText(qrData.qr_data); toast.success('Copied'); }}>
                <Copy className="w-3 h-3 mr-1" />Copy QR Data
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Passport QR Book Modal */}
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-400" />Generate Passport QR Book
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Step 6: Create a batch of DORMANT Passport QR labels to ship with device
            </DialogDescription>
          </DialogHeader>

          {bookResult ? (
            <div className="space-y-4 py-2">
              <div className="text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="text-green-400 font-semibold">QR Book Generated</p>
                <p className="text-slate-400 text-sm">{bookResult.total_labels} DORMANT labels for {bookResult.tenant_name}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Batch</span><span className="font-mono text-white">{bookResult.batch_number}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Labels</span><span className="text-purple-400 font-bold">{bookResult.total_labels}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Status</span><Badge className="bg-amber-900/30 text-amber-400">DORMANT</Badge></div>
                <div>
                  <p className="text-slate-500 text-xs mb-1">Sample UIDs:</p>
                  {bookResult.sample_labels?.map((l) => (
                    <span key={l} className="inline-block font-mono text-xs text-slate-300 bg-slate-800 px-2 py-0.5 rounded mr-1 mb-1">{l}</span>
                  ))}
                </div>
              </div>
              <Button onClick={() => { setShipOpen(true); setBookOpen(false); }} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="proceed-to-ship-btn">
                <Truck className="w-4 h-4 mr-2" />Ship Device + QR Book to Tenant
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Tenant / Client</label>
                <select value={bookTenantId} onChange={(e) => setBookTenantId(e.target.value)} className="w-full rounded-md border border-slate-600 bg-slate-900 text-white p-2.5" data-testid="book-tenant-select">
                  <option value="">Select tenant...</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Number of Labels</label>
                <Input type="number" value={bookCount} onChange={(e) => setBookCount(parseInt(e.target.value) || 1000)} min={100} max={10000} className="bg-slate-900 border-slate-600 text-white" data-testid="book-count-input" />
                <p className="text-xs text-slate-500 mt-1">Min 100, Max 10,000. Labels start as DORMANT until dispatcher activates.</p>
              </div>
            </div>
          )}

          {!bookResult && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setBookOpen(false)} className="border-slate-600 text-slate-300">Cancel</Button>
              <Button onClick={handleGenerateBook} disabled={generatingBook || !bookTenantId} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-generate-book-btn">
                {generatingBook ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BookOpen className="w-4 h-4 mr-2" />}
                Generate {bookCount} Labels
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Ship Modal */}
      <Dialog open={shipOpen} onOpenChange={setShipOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Truck className="w-5 h-5 text-teal-400" />Ship to Tenant
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Step 7: Package device + Passport QR roll and ship together
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Tracking Number (optional)</label>
              <Input value={shipTracking} onChange={(e) => setShipTracking(e.target.value)} placeholder="e.g. DTDC123456" className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Courier (optional)</label>
              <Input value={shipCourier} onChange={(e) => setShipCourier(e.target.value)} placeholder="e.g. DTDC, BlueDart, FedEx" className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <p className="text-xs text-slate-500">Tenant will receive the hardware device (with Device QR on body) + Passport QR label roll. They can begin dispatch workflow immediately.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShipOpen(false)} className="border-slate-600 text-slate-300">Cancel</Button>
            <Button onClick={handleShip} disabled={shipping} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-ship-btn">
              {shipping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Ship Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
